import { number, arrayOf, string, shape, func, bool } from 'prop-types';
import React, { Fragment, useState } from 'react';
import { Mutation } from 'react-apollo';

import withUserAuthentication from 'lib/withUserAuthentication';
import Modal from 'components/Modal';
import Teaser from 'components/Teaser';
import printCleanCookTime from 'util/recipes';
import IconArrow from 'components/Icons/Arrow';
import { RecipeBoxRecipeEditForm } from 'components/RecipeBoxRecipeEditForm';
import RecipeRating from 'components/RecipeRating';
import { MUTATION } from 'components/RecipeRatingsDataProvider';
import { updateRatingInCache } from 'components/RecipeBoxRecipeEditForm/helpers';
import { truncate } from 'util/string';

import UPDATE_RECIPE_IN_BOX_MUTATION from 'graphql/updateRecipeInBox';
import DELETE_USER_RECIPE_FROM_BOX_MUTATION from 'graphql/deleteUserRecipeFromBox';

import TeaserInfoWrapper from './TeaserInfoWrapper';
import styles from './styles';

export function TeaserRecipeCard(props) {
  const {
    url,
    headline,
    recipeId,
    userRecipeId,
    userTags,
    userNotes,
    onRecipeBoxDataChange,
    prepTimeMin,
    prepTimeMax,
    cookTimeMin,
    cookTimeMax,
    user,
    hasEditControl,
    ratingsData,
    userRatingsData,
    isRateable
  } = props;

  const [hasShowEditForm, setHasShowEditForm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // Flag used to do an optimistic deletion by hidding the grid item
  const [isDeleted, setIsDeleted] = useState(false);

  const toggleEditForm = e => {
    if (e) e.preventDefault();
    setHasShowEditForm(!hasShowEditForm);
  };

  const gtmAttrs = {
    'data-gtm-recipebox-user': (user && user.auth0User) || '',
    'data-gtm-recipebox-recipe-url': url
  };

  const recipeTime = printCleanCookTime(
    prepTimeMin,
    prepTimeMax,
    cookTimeMin,
    cookTimeMax
  );

  const userTagsIds = userTags.map(t => t.recipeTagId);
  const userTagNames = userTags.map(t => t.name).join(' | ');

  const teaserProps = {
    ...props,
    variant: 'recipeBoxSearchResult',
    fallbackDims: {
      height: 200,
      width: 300
    },
    sourceSets: [
      {
        imageDims: {
          height: 405.5,
          width: 608
        },
        layoutWidth: {
          units: 'vw',
          value: 39
        },
        media: '(min-width: 64rem)'
      },
      {
        imageDims: {
          height: 405.5,
          width: 608
        },
        layoutWidth: {
          units: 'vw',
          value: 100
        },
        media: '(max-width: 63.99rem)'
      }
    ],
    gtmAttrs,
    dek: recipeTime,
    kicker: userTagNames,
    placeholders: [
      {
        media: '(min-width: 0)',
        ratio: 1.5
      }
    ]
  };

  if (isDeleted) {
    return null;
  }

  const editFormProps = {
    url,
    headline,
    recipeId,
    userRecipeId,
    userTagsIds,
    userNotes,
    onRecipeBoxDataChange,
    toggleEditForm,
    setIsDeleted,
    setIsUpdating,
    ratingsData,
    userRatingsData
  };

  const onClick = hasEditControl ? e => toggleEditForm(e) : undefined;

  return (
    <Fragment>
      <div className="TeaserRecipeCard">
        <Teaser
          {...teaserProps}
          tags={undefined}
          isRecipeBoxTeaser
          data-test="recipeBoxTeaser"
          onClick={onClick}
        />
        <div className="TeaserRecipeCard__recipeInfo">
          <TeaserInfoWrapper
            isLink={hasEditControl}
            onClick={e => {
              e.preventDefault();
              toggleEditForm(e);
            }}
          >
            <Fragment>
              <div className="TeaserRecipeCard__recipeRating">
                <Mutation mutation={MUTATION}>
                  {(mutate, { loading: ratingLoading }) => (
                    <RecipeRating
                      ratingsData={ratingsData}
                      userRatingsData={userRatingsData}
                      rateRecipeIsLoading={ratingLoading}
                      recipeId={parseInt(recipeId, 10)}
                      rateRecipe={userRating =>
                        mutate({
                          variables: {
                            recipeId: parseInt(recipeId, 10),
                            userRating
                          },
                          update: updateRatingInCache
                        })
                      }
                      isRateable={isRateable}
                      placement="recipe box"
                    />
                  )}
                </Mutation>
              </div>
              {userNotes && hasEditControl && (
                <div className="TeaserRecipeCard__recipeNotes">
                  {truncate(userNotes, 30)}
                </div>
              )}
            </Fragment>
          </TeaserInfoWrapper>
        </div>
        {isUpdating && (
          <div className="TeaserRecipeCard__isUpdatingMessageBox">
            <span>Saving...</span>
          </div>
        )}
      </div>
      {hasShowEditForm && (
        <div className="TeaserRecipeCard__editForm">
          <Modal
            onClose={toggleEditForm}
            cssHook="TeaserRecipeCard__editFormModal"
          >
            <div className="TeaserRecipeCard__editFormHeader">
              <a href="#" onClick={e => toggleEditForm(e)}>
                <IconArrow rotate={180} />
                <span>Back</span>
              </a>
            </div>
            <Mutation mutation={UPDATE_RECIPE_IN_BOX_MUTATION}>
              {updateRecipeInBox => (
                <Mutation mutation={DELETE_USER_RECIPE_FROM_BOX_MUTATION}>
                  {removeRecipeFromBox => (
                    <RecipeBoxRecipeEditForm
                      {...editFormProps}
                      onUpdateRecipeInBox={updateRecipeInBox}
                      onRemoveRecipeFromBox={removeRecipeFromBox}
                    />
                  )}
                </Mutation>
              )}
            </Mutation>
          </Modal>
        </div>
      )}
      <style jsx>{styles}</style>
    </Fragment>
  );
}

TeaserRecipeCard.propTypes = {
  ...Teaser.propTypes,
  index: string,
  recipeId: string,
  userRecipeId: string,
  userTags: arrayOf(
    shape({
      name: string,
      recipeTagId: string
    })
  ),
  userNotes: string,
  ratingData: shape({}),
  cookTimeMax: number,
  cookTimeMin: number,
  prepTimeMax: number,
  tags: arrayOf(string),
  prepTimeMin: number,
  user: shape({
    auth0User: string.isRequired
  }),
  onRecipeBoxDataChange: func,
  hasEditControl: bool,
  isRateable: bool
};

TeaserRecipeCard.defaultProps = {
  ...Teaser.defaultProps,
  recipeId: null,
  userRecipeId: null,
  userTags: null,
  userNotes: null,
  ratingData: null,
  cookTimeMax: null,
  cookTimeMin: null,
  prepTimeMax: null,
  prepTimeMin: null,
  tags: [],
  user: undefined,
  hasEditControl: true,
  onRecipeBoxDataChange: () => false,
  isRateable: true
};

export default withUserAuthentication(TeaserRecipeCard);
